import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CalendarDays, CheckCircle2, XCircle, User, FileText, X, Clock, Users, Ban, Search,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Button, Textarea, Pill, Spinner, EmptyState, KPI, Input, Select,
} from "@/components/ui";
import type { Tone } from "@/components/ui";

// =============================================================================
// LeaveApprovalsScreen — /hr/leave-approvals  (manager+)
// =============================================================================
// James, 21 Sep 2026: "not just approvals but history of leave taken, who's
// got upcoming leave, employee balances at a glance, anything leave related …
// one clean user friendly dashboard for the manager to use in HR and not
// just 'Approvals' then gone once approved."
//
// Four tabs on one screen:
//   Pending  — the queue (approve / decline), as before, with the live balance
//   Upcoming — approved leave from today forward, who is off when
//   History  — every request, any status, searchable; a manager can cancel an
//              approved one (mig 383: that also removes its timesheet days)
//   Balances — v_leave_position for every active employee (330: calendar
//              accrual from the start date, minus approved leave)
// Approved leave writes 7.6 h / 0 h days onto the timesheet (mig 383), so
// Team week and payroll see it — this screen is where you see the requests.
// =============================================================================

type Status = "draft" | "submitted" | "approved" | "rejected" | "cancelled" | "taken";

interface LeaveRow {
  request_id:      string;
  request_number:  string;
  employee_id:     string;
  leave_type_code: string;
  starts_on:       string;
  ends_on:         string;
  days_requested:  number;
  reason:          string | null;
  status:          Status;
  submitted_at:    string | null;
  approved_at:     string | null;
  approved_by_user_id: string | null;
  rejection_notes: string | null;
  employee?:   { first_name: string | null; last_name: string | null } | null;
  leave_type?: { display_name: string; is_paid: boolean } | null;
  balance?:    Balance | null;
}

interface Balance {
  employee_id: string; employment_type: string; accrues: boolean; has_opening: boolean; as_at: string;
  al_days: number; al_accrued: number; al_taken: number;
  sl_days: number; sl_accrued: number; sl_taken: number;
  lsl_weeks: number; rdo_days: number;
  first_name?: string | null; last_name?: string | null;
}

const STATUS_TONE: Record<Status, Tone> = {
  draft: "default", submitted: "pending", approved: "approved", rejected: "rejected", cancelled: "default", taken: "ok",
};

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate().toString().padStart(2, "0")}/${(dt.getMonth() + 1).toString().padStart(2, "0")}/${dt.getFullYear()}`;
}
function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
function empName(r: { employee?: { first_name: string | null; last_name: string | null } | null }): string {
  return r.employee ? `${r.employee.first_name ?? ""} ${r.employee.last_name ?? ""}`.trim() || "(unknown)" : "(unknown)";
}
const todayIso = () => new Date().toISOString().slice(0, 10);

// The seeded leave_types codes are AL/SL/LSL/RDO — and the question a
// director is actually asking: what's left AFTER I approve this one.
function balanceLabel(code: string, days: number, b: Balance | null | undefined): string {
  if (!b) return "no balance on file";
  const line = (have: number, unit: string, takes: number) => {
    const after = have - takes;
    return `${have.toFixed(1)} ${unit} → ${after.toFixed(1)} after${after < 0 ? " (over)" : ""}`;
  };
  switch (code) {
    case "AL":  return line(Number(b.al_days),   "days",  days);
    case "SL":  return line(Number(b.sl_days),   "days",  days);
    case "LSL": return line(Number(b.lsl_weeks), "weeks", days / 5);
    case "RDO": return line(Number(b.rdo_days),  "days",  days);
    default:    return "not balance-tracked";
  }
}

type Tab = "pending" | "upcoming" | "history" | "balances";

export default function LeaveApprovalsScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canApprove = hasRole(user, "manager");
  const [tab, setTab] = useState<Tab>("pending");

  // Every request (any status) — one query feeds all four tabs. 1000 is years of
  // leave for a crew this size.
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["leave_all_requests"],
    enabled: !!user && canApprove,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leave_requests")
        .select(`
          request_id, request_number, employee_id, leave_type_code,
          starts_on, ends_on, days_requested, reason, status, submitted_at, approved_at, approved_by_user_id, rejection_notes,
          employee:employee_id (first_name, last_name),
          leave_type:leave_types (display_name, is_paid)
        `)
        .order("starts_on", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as unknown as LeaveRow[];
    },
  });

  // Balances for everyone active (330). Names come from employees.
  const { data: balances = [] } = useQuery({
    queryKey: ["leave_positions_all"],
    enabled: !!user && canApprove,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_leave_position" as any)
        .select("employee_id, employment_type, accrues, has_opening, as_at, al_days, al_accrued, al_taken, sl_days, sl_accrued, sl_taken, lsl_weeks, rdo_days");
      if (error) throw error;
      const list = (data ?? []) as any[] as Balance[];
      const ids = list.map(b => b.employee_id);
      if (ids.length) {
        const { data: emps } = await supabase.from("employees").select("employee_id, first_name, last_name").in("employee_id", ids);
        const byId = new Map((emps ?? []).map((e: any) => [e.employee_id, e]));
        list.forEach(b => { const e = byId.get(b.employee_id); b.first_name = e?.first_name ?? null; b.last_name = e?.last_name ?? null; });
      }
      return list.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
    },
  });
  const balanceByEmp = useMemo(() => new Map(balances.map(b => [b.employee_id, b])), [balances]);

  const today = todayIso();
  const pending  = useMemo(() => rows.filter(r => r.status === "submitted").sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? "")), [rows]);
  const upcoming = useMemo(() => rows.filter(r => (r.status === "approved" || r.status === "taken") && r.ends_on >= today).sort((a, b) => a.starts_on.localeCompare(b.starts_on)), [rows, today]);
  const offNow   = useMemo(() => upcoming.filter(r => r.starts_on <= today), [upcoming, today]);
  const in30     = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 30); const lim = d.toISOString().slice(0, 10); return upcoming.filter(r => r.starts_on > today && r.starts_on <= lim); }, [upcoming, today]);

  if (!user) return null;
  if (!canApprove) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-[13px] text-text-muted">Only directors and managers can see the leave dashboard.</div></Card>
      </div>
    );
  }

  const TABS: Array<[Tab, string, number | null]> = [
    ["pending",  "Pending",  pending.length],
    ["upcoming", "Upcoming", upcoming.length],
    ["history",  "History",  null],
    ["balances", "Balances", null],
  ];

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto pb-12">
      <button onClick={() => navigate("/hr")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> HR hub
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
          <CalendarDays size={22} />
        </div>
        <div>
          <h1 className="text-[24px] font-semibold text-text">Leave</h1>
          <p className="text-[13px] text-text-muted mt-0.5">Requests to approve, who's off and when, every request on file, and balances.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="To approve" value={String(pending.length)} icon={Clock} accent={pending.length > 0} />
        <KPI label="Off right now" value={String(offNow.length)} icon={Users} sub={offNow.slice(0, 2).map(empName).join(", ") || "nobody"} />
        <KPI label="Next 30 days" value={String(in30.length)} icon={CalendarDays} sub={`${in30.reduce((a, r) => a + Number(r.days_requested), 0).toFixed(1)} days booked`} />
        <KPI label="On file" value={String(rows.length)} sub={`${rows.filter(r => r.status === "approved" || r.status === "taken").length} approved`} />
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-line overflow-x-auto">
        {TABS.map(([key, label, n]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap ${tab === key ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text"}`}>
            {label}{n != null && n > 0 ? <span className="ml-1.5 font-mono text-[11px] text-accent">{n}</span> : null}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card><div className="py-10 flex justify-center"><Spinner /></div></Card>
      ) : tab === "pending" ? (
        <Card>
          {pending.length === 0
            ? <EmptyState icon={CheckCircle2} title="Nothing pending" description="All leave requests are up to date." />
            : <div className="divide-y divide-line">{pending.map(r => <PendingRowCard key={r.request_id} r={{ ...r, balance: balanceByEmp.get(r.employee_id) ?? null }} />)}</div>}
        </Card>
      ) : tab === "upcoming" ? (
        <UpcomingTab rows={upcoming} today={today} />
      ) : tab === "history" ? (
        <HistoryTab rows={rows} />
      ) : (
        <BalancesTab balances={balances} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Upcoming — who is off, grouped: now / this month / later
// -----------------------------------------------------------------------------
function UpcomingTab({ rows, today }: { rows: LeaveRow[]; today: string }) {
  const qc = useQueryClient();
  if (rows.length === 0) return <Card><EmptyState icon={CalendarDays} title="No leave booked" description="Approved leave from today onward shows here." /></Card>;
  const groups: Array<[string, LeaveRow[]]> = [
    ["Off now", rows.filter(r => r.starts_on <= today)],
    ["Coming up", rows.filter(r => r.starts_on > today)],
  ];
  return (
    <div className="space-y-4">
      {groups.filter(([, g]) => g.length).map(([label, g]) => (
        <Card key={label}>
          <div className="px-4 pt-3 text-[11px] font-mono uppercase tracking-wider2 text-text-muted">{label} · {g.length}</div>
          <div className="divide-y divide-line">
            {g.map(r => <LeaveLine key={r.request_id} r={r} canCancel onChanged={() => { qc.invalidateQueries({ queryKey: ["leave_all_requests"] }); qc.invalidateQueries({ queryKey: ["leave_positions_all"] }); }} />)}
          </div>
        </Card>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// History — everything, filter by person / status / search
// -----------------------------------------------------------------------------
function HistoryTab({ rows }: { rows: LeaveRow[] }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | Status>("all");
  const [emp, setEmp] = useState("all");
  const people = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach(r => m.set(r.employee_id, empName(r)));
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (status !== "all" && r.status !== status) return false;
      if (emp !== "all" && r.employee_id !== emp) return false;
      if (needle && ![empName(r), r.request_number, r.leave_type?.display_name ?? "", r.leave_type_code, r.reason ?? "", r.rejection_notes ?? ""].join(" ").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [rows, q, status, emp]);
  return (
    <div>
      <Card className="mb-4">
        <div className="p-3 grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2 relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, request no., type, reason…" className="pl-8" />
          </div>
          <Select value={emp} onChange={e => setEmp(e.target.value)}>
            <option value="all">Everyone</option>
            {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </Select>
          <Select value={status} onChange={e => setStatus(e.target.value as any)}>
            <option value="all">Any status</option>
            <option value="submitted">Pending</option>
            <option value="approved">Approved</option>
            <option value="taken">Taken</option>
            <option value="rejected">Declined</option>
            <option value="cancelled">Cancelled</option>
            <option value="draft">Draft</option>
          </Select>
        </div>
      </Card>
      <Card>
        {shown.length === 0
          ? <EmptyState icon={FileText} title="No requests match" description="Try clearing the filters." />
          : <div className="divide-y divide-line">{shown.map(r => <LeaveLine key={r.request_id} r={r} canCancel onChanged={() => { qc.invalidateQueries({ queryKey: ["leave_all_requests"] }); qc.invalidateQueries({ queryKey: ["leave_positions_all"] }); }} />)}</div>}
      </Card>
    </div>
  );
}

// One request, any status. Approved/taken ones can be cancelled by a manager.
function LeaveLine({ r, canCancel, onChanged }: { r: LeaveRow; canCancel: boolean; onChanged: () => void }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancellable = canCancel && (r.status === "approved" || r.status === "taken");
  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium text-text">{empName(r)}</span>
          <Pill label={r.leave_type?.display_name ?? r.leave_type_code} tone={r.leave_type?.is_paid === false ? "warning" : "open"} small />
          {r.leave_type?.is_paid === false && <span className="text-[10px] text-text-muted">not deducted from a balance</span>}
          <Pill label={r.status === "submitted" ? "pending" : r.status === "rejected" ? "declined" : r.status} tone={STATUS_TONE[r.status]} small />
          <span className="font-mono text-[11px] text-text-muted">{r.request_number}</span>
        </div>
        <div className="text-[12px] text-text-muted mt-0.5">
          {fmtDate(r.starts_on)} → {fmtDate(r.ends_on)} · {Number(r.days_requested).toFixed(1)} day{Number(r.days_requested) === 1 ? "" : "s"}
          {r.approved_at && <span> · approved {fmtDate(r.approved_at)}</span>}
        </div>
        {r.reason && <div className="text-[12px] text-text mt-1">{r.reason}</div>}
        {r.rejection_notes && (r.status === "rejected" || r.status === "cancelled") && <div className="text-[12px] text-danger mt-1">{r.status === "rejected" ? "Declined" : "Cancelled"}: {r.rejection_notes}</div>}
      </div>
      {cancellable && (
        <Button variant="secondary" size="sm" icon={Ban} onClick={() => setCancelOpen(true)}>Cancel leave</Button>
      )}
      {cancelOpen && <CancelModal request={r} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); onChanged(); }} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Balances — everyone, at a glance
// -----------------------------------------------------------------------------
// 2 Nov 2026 — James: "we should have hrs here as the balance, as that is how
// leave is tracked; a day is 7.6". Balances are kept in days underneath (the
// award view); the table shows hours at 7.6 h a day with the days in small print.
const HOURS_PER_DAY = 7.6;
function BalancesTab({ balances }: { balances: Balance[] }) {
  if (balances.length === 0) return <Card><EmptyState icon={Users} title="No balances" description="Balances come from each employee's start date (or an entered opening) minus approved leave." /></Card>;
  const fmt = (n: number) => Number(n).toFixed(1);
  const hrs = (days: number) => (Number(days) * HOURS_PER_DAY).toFixed(1);
  const cell = (days: number, accrued?: number, taken?: number) => (
    <td className={`px-4 py-2.5 text-right font-mono ${Number(days) < 0 ? "text-danger" : ""}`} title={accrued != null ? `accrued ${hrs(accrued)} h · taken ${hrs(taken ?? 0)} h` : undefined}>
      {hrs(days)}<div className="text-[10px] text-text-muted font-sans">{fmt(days)} d</div>
    </td>
  );
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider2 text-text-muted border-b border-line">
              <th className="px-4 py-2.5 font-medium">Employee</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium text-right">Annual (hrs)</th>
              <th className="px-4 py-2.5 font-medium text-right">Personal (hrs)</th>
              <th className="px-4 py-2.5 font-medium text-right">LSL (wks)</th>
              <th className="px-4 py-2.5 font-medium text-right">RDO (hrs)</th>
              <th className="px-4 py-2.5 font-medium">Counted from</th>
            </tr>
          </thead>
          <tbody>
            {balances.map(b => (
              <tr key={b.employee_id} className="border-b border-line/60">
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">{b.first_name} {b.last_name}</td>
                <td className="px-4 py-2.5 text-text-muted">{(b.employment_type ?? "").replace(/_/g, " ")}{!b.accrues && <span className="text-[11px]"> · no accrual</span>}</td>
                {cell(b.al_days, b.al_accrued, b.al_taken)}
                {cell(b.sl_days, b.sl_accrued, b.sl_taken)}
                <td className="px-4 py-2.5 text-right font-mono">{fmt(b.lsl_weeks)}</td>
                {cell(b.rdo_days)}
                <td className="px-4 py-2.5 text-text-muted whitespace-nowrap">{fmtDate(b.as_at)}{b.has_opening ? " (opening entered)" : " (start date)"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 text-[11px] text-text-muted">Hours at {HOURS_PER_DAY} h a day (152 annual / 76 personal hours a year of service, pro-rata to the day, minus approved leave). Unpaid leave never comes off a balance. Hover a figure for accrued vs taken. Enter an opening balance on the employee's file to override the start-date count.</div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Pending row — approve / decline (unchanged behaviour)
// -----------------------------------------------------------------------------
function PendingRowCard({ r }: { r: LeaveRow }) {
  const qc = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["leave_all_requests"] });
    qc.invalidateQueries({ queryKey: ["leave_positions_all"] });
    qc.invalidateQueries({ queryKey: ["my_leave_requests"] });
    qc.invalidateQueries({ queryKey: ["my_leave_position"] });
    qc.invalidateQueries({ queryKey: ["team_week_entries"] });
  };

  const approveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema("app").rpc("approve_leave_request", { p_request_id: r.request_id });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => alert(e?.message ?? e?.error_description ?? e?.hint ?? e?.details ?? "Approval failed"),
  });

  const typeName = r.leave_type?.display_name ?? r.leave_type_code;
  const bal = balanceLabel(r.leave_type_code, Number(r.days_requested), r.balance);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-accent/12 text-accent flex items-center justify-center flex-shrink-0"><User size={15} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14px] font-medium text-text">{empName(r)}</span>
              <span className="font-mono text-[11px] text-text-muted">{r.request_number}</span>
              <Pill label={typeName} tone={r.leave_type?.is_paid === false ? "warning" : "open"} small />
            </div>
            <div className="text-[12px] text-text-muted mt-0.5">
              {fmtDate(r.starts_on)} → {fmtDate(r.ends_on)} · {Number(r.days_requested).toFixed(1)} day{Number(r.days_requested) === 1 ? "" : "s"}
              {r.submitted_at && <span> · submitted {timeAgo(r.submitted_at)}</span>}
            </div>
            <div className="text-[11px] text-text-muted mt-0.5 font-mono">Balance: {bal}</div>
            <div className="text-[11px] text-text-muted mt-0.5">On approval each weekday goes on the timesheet as {r.leave_type?.is_paid === false ? "0 h (unpaid)" : "a paid day"}.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="secondary" size="sm" icon={XCircle} onClick={() => setRejectOpen(true)} disabled={approveMut.isPending}>Decline</Button>
          <Button size="sm" icon={CheckCircle2} onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>{approveMut.isPending ? "Approving…" : "Approve"}</Button>
        </div>
      </div>
      {r.reason && (
        <div className="mt-2 ml-12 p-2 rounded bg-ink-raised border border-line text-[12px] text-text flex items-start gap-2">
          <FileText size={12} className="text-text-muted flex-shrink-0 mt-0.5" /><span>{r.reason}</span>
        </div>
      )}
      {rejectOpen && <RejectModal request={r} onClose={() => setRejectOpen(false)} onDone={() => { setRejectOpen(false); invalidate(); }} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
function ReasonModal({ title, blurb, placeholder, confirmLabel, danger, onClose, run }: {
  title: string; blurb: string; placeholder: string; confirmLabel: string; danger?: boolean; onClose: () => void;
  run: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => { if (!reason.trim()) throw new Error("Please give the employee a short reason."); await run(reason.trim()); },
    onError: (e: any) => setErr(e?.message ?? e?.error_description ?? e?.hint ?? e?.details ?? "Failed"),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <Card>
          <div className="p-5 border-b border-line flex items-center justify-between">
            <div className="text-[14px] font-medium text-text">{title}</div>
            <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18} /></button>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-[12px] text-text-muted">{blurb}</div>
            <Textarea rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder={placeholder} autoFocus />
            {err && <div className="text-[12px] text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{err}</div>}
          </div>
          <div className="p-4 border-t border-line flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button variant={danger ? "danger" : "primary"} onClick={() => mut.mutate()} disabled={!reason.trim() || mut.isPending} type="button">
              {mut.isPending ? "Working…" : confirmLabel}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function RejectModal({ request, onClose, onDone }: { request: LeaveRow; onClose: () => void; onDone: () => void }) {
  return (
    <ReasonModal
      title="Decline leave request"
      blurb={`The reason below is sent to ${empName(request)} as an in-app notification so they know why.`}
      placeholder="e.g. Crew availability clash — resubmit for the following week?"
      confirmLabel="Decline" danger onClose={onClose}
      run={async (reason) => {
        const { error } = await supabase.schema("app").rpc("reject_leave_request", { p_request_id: request.request_id, p_reason: reason });
        if (error) throw error;
        onDone();
      }}
    />
  );
}

function CancelModal({ request, onClose, onDone }: { request: LeaveRow; onClose: () => void; onDone: () => void }) {
  return (
    <ReasonModal
      title="Cancel approved leave"
      blurb={`${empName(request)}'s ${request.leave_type?.display_name ?? request.leave_type_code} ${fmtDate(request.starts_on)} → ${fmtDate(request.ends_on)} is cancelled, its days come off the timesheet, and they are told why.`}
      placeholder="e.g. Needed on site that week — rebook for the following one"
      confirmLabel="Cancel the leave" danger onClose={onClose}
      run={async (reason) => {
        const { error } = await supabase.schema("app").rpc("cancel_approved_leave" as any, { p_request_id: request.request_id, p_reason: reason } as any);
        if (error) throw error;
        onDone();
      }}
    />
  );
}
